import React, { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedNumber } from 'react-intl';
import intlShape from '@/shared/utils/intlShape';
import classNames from 'classnames';
import merge from 'lodash-es/merge';
import RelationName from '@/shared/components/RelationName';
import FormattedCurrency from '@/shared/components/FormattedCurrency';
import { mapInvoiceStreamName } from '@/shared/services/ObjectDisplayService';
import EditableCurrency from '@/shared/components/editable/EditableCurrency';
import EditableNumber from '@/shared/components/editable/EditableNumber';
import { FaTrashAlt, FaRegWindowRestore, FaHistory } from 'react-icons/fa';
import Checkbox from '@/shared/components/input/Checkbox';
import { CanRead } from '@/shared/components/security';
import { StateService } from '@/shared/services/StateService';
import BudgetGraph from './BudgetGraph';
import { getPercentageColorClass, getUsedBudgetPercentage } from './budgetHelpers';
import messages from './budgetMessages';
import NavigateToState from '../../../shared/components/NavigateToState';
import { useRegistrationOverviewNavParams } from './hooks/useRegistrationOverviewNavParams';

const resourceTimeFilter = {
  filters: {
    type: { items: [0] },
  },
};
const BudgetRow = ({
  intl,
  value,
  showAssignments,
  onUpdateBudget,
  onDeleteBudget,
  canUpdateAmounts,
  canUpdateHours,
  canViewAmounts,
  canViewHours,
  handleSelect,
  selected,
  disableSelect,
  invoiceDialogsSvc,
  showBudgetGraphModal,
  stateService,
  columnStatus,
}) => {
  const { relation, invoiceStream, budget = null } = value;
  const regNavParams = useRegistrationOverviewNavParams(
    budget.year,
    relation.id,
    invoiceStream.id,
    invoiceStream.companyId ?? relation.companyId,
  );
  if (!budget) {
    return (
      <tbody className="hover" key={invoiceStream.id}>
        <tr>
          <td>
            <Checkbox
              onClick={() => handleSelect(invoiceStream.id)}
              value={selected}
              disabled={disableSelect}
            />
          </td>
          <td>
            <RelationName tabIndex={-1} relation={relation} />
          </td>
          {showAssignments && (
            <td className="fw-normal">{mapInvoiceStreamName(invoiceStream, intl)}</td>
          )}
          <td colSpan="9" />
          {(canUpdateAmounts || canUpdateHours) && <td />}
        </tr>
      </tbody>
    );
  }

  const amountPct = getUsedBudgetPercentage(budget.budgetAmount, budget.actualAmount);
  const hoursPct = getUsedBudgetPercentage(budget.budgetHours, budget.actualHours);
  const budgetClass = budget.isNew ? 'text-muted fw-normal' : 'fw-normal';

  return (
    <tbody className="hover" key={invoiceStream.id}>
      <tr>
        <td>
          <Checkbox
            onClick={() => handleSelect(invoiceStream.id)}
            value={selected}
            disabled={disableSelect}
          />
        </td>
        <td>
          <RelationName tabIndex={-1} relation={relation} />
        </td>
        {showAssignments && (
          <td className="fw-normal">{mapInvoiceStreamName(invoiceStream, intl)}</td>
        )}
        <td className={budgetClass}>{budget.year}</td>
        {canViewAmounts ? (
          <Fragment>
            {columnStatus.budgetAmount && (
              <td className="text-right">
                <EditableCurrency
                  value={budget.budgetAmount}
                  onChange={val => onUpdateBudget({ ...budget, budgetAmount: val })}
                  readOnly={!canUpdateAmounts}
                  min={0}
                  max={99999999999.99}
                />
              </td>
            )}
            {columnStatus.actualAmount && (
              <td className={classNames('text-right', budgetClass)}>
                {budget.budgetAmount || budget.actualAmount ? (
                  <NavigateToState
                    stateName="registrations.overview"
                    useUrl
                    paramsToJson
                    title={intl.formatMessage(messages.ShowInRegistrationOverview)}
                    functionality="RegistrationOthers"
                    permission="Read"
                    stateParams={regNavParams}
                  >
                    <FormattedCurrency className="fw-normal" value={budget.actualAmount || 0} />
                  </NavigateToState>
                ) : null}
              </td>
            )}
            {columnStatus.remainingAmount && (
              <td className={classNames('text-right', budgetClass)}>
                {budget.budgetAmount || budget.actualAmount ? (
                  <FormattedNumber
                    className="fw-normal"
                    value={(budget.budgetAmount ?? 0) - (budget.actualAmount ?? 0)}
                    maximumFractionDigits={2}
                  />
                ) : null}
              </td>
            )}
            {columnStatus.budgetAmountUsedPct && (
              <td className={classNames('text-right', getPercentageColorClass(amountPct))}>
                {budget.budgetAmount ? (
                  <span className="fw-normal">
                    <FormattedNumber value={amountPct} style="percent" />
                  </span>
                ) : null}
              </td>
            )}
            {canViewAmounts && columnStatus.budgetAmountUsedPct && (
              <td className="text-right">
                {budget.budgetAmount ? (
                  <BudgetGraph
                    onClick={() =>
                      showBudgetGraphModal(
                        value.amountPerMonth,
                        budget.budgetAmount,
                        budget.year,
                        true,
                      )
                    }
                    data={value.amountPerMonth}
                    budget={budget.budgetAmount}
                  />
                ) : null}
              </td>
            )}
          </Fragment>
        ) : null}
        {canViewHours ? (
          <>
            {columnStatus.budgetHours && (
              <td className="text-right">
                <EditableNumber
                  value={budget.budgetHours}
                  allowDecimals
                  onChange={val => onUpdateBudget({ ...budget, budgetHours: val })}
                  readOnly={!canUpdateHours}
                />
              </td>
            )}
            {columnStatus.actualHours && (
              <td className={classNames('text-right', budgetClass)}>
                {budget.budgetHours || budget.actualHours ? (
                  <NavigateToState
                    stateName="registrations.overview"
                    useUrl
                    paramsToJson
                    title={intl.formatMessage(messages.ShowInRegistrationOverview)}
                    functionality="RegistrationOthers"
                    permission="Read"
                    stateParams={merge({}, regNavParams, resourceTimeFilter)}
                  >
                    <span className="fw-normal">
                      <FormattedNumber
                        className="fw-normal"
                        value={budget.actualHours || 0}
                        maximumFractionDigits={2}
                      />
                    </span>
                  </NavigateToState>
                ) : null}
              </td>
            )}
            {columnStatus.remainingHours && (
              <td className={classNames('text-right', budgetClass)}>
                {budget.budgetHours || budget.actualHours ? (
                  <FormattedNumber
                    className="fw-normal"
                    value={(budget.budgetHours ?? 0) - (budget.actualHours ?? 0)}
                    maximumFractionDigits={2}
                  />
                ) : null}
              </td>
            )}
            {columnStatus.budgetHoursUsedPct && (
              <td className={classNames('text-right', getPercentageColorClass(hoursPct))}>
                {budget.budgetHours ? (
                  <span className="fw-normal">
                    <FormattedNumber value={hoursPct} style="percent" />
                  </span>
                ) : null}
              </td>
            )}
            {columnStatus.budgetHoursUsedPct && (
              <td>
                {budget.budgetHours ? (
                  <BudgetGraph
                    data={value.hoursPerMonth}
                    budget={budget.budgetHours}
                    onClick={() =>
                      showBudgetGraphModal(
                        value.hoursPerMonth,
                        budget.budgetHours,
                        budget.year,
                        false,
                      )
                    }
                  />
                ) : null}
              </td>
            )}
          </>
        ) : null}
        <td className="text-right actions-column">
          <CanRead on="RegistrationOthers">
            <a
              className="btn btn-xs btn-icon"
              title={intl.formatMessage(messages.ShowInRegistrationOverview)}
              onClick={() => stateService.gotoRegistrationOverview(regNavParams)}
            >
              <FaRegWindowRestore />
            </a>
          </CanRead>
          <CanRead on="InvoiceDraft">
            <a
              className="btn btn-xs btn-icon"
              title={intl.formatMessage(messages.ShowInvoicingHistory)}
              onClick={() =>
                invoiceDialogsSvc.showInvoicingHistory(invoiceStream.id, null, relation.id)
              }
            >
              <FaHistory />
            </a>
          </CanRead>
          {(canUpdateAmounts || canUpdateHours) && (
            <a
              className="btn btn-xs btn-icon"
              title={intl.formatMessage(messages.DeleteBudget)}
              onClick={() => onDeleteBudget(value)}
            >
              <FaTrashAlt />
            </a>
          )}
        </td>
      </tr>
    </tbody>
  );
};

BudgetRow.propTypes = {
  intl: intlShape.isRequired,
  value: PropTypes.shape({}).isRequired,
  showAssignments: PropTypes.bool,
  canUpdateBudget: PropTypes.bool,
  canViewAmounts: PropTypes.bool,
  onUpdateBudget: PropTypes.func.isRequired,
  onDeleteBudget: PropTypes.func.isRequired,
  handleSelect: PropTypes.func.isRequired,
  selected: PropTypes.bool,
  disableSelect: PropTypes.bool,
  invoiceDialogsSvc: PropTypes.shape({}).isRequired,
  showBudgetGraphModal: PropTypes.func.isRequired,
  stateService: PropTypes.instanceOf(StateService).isRequired,
  columnStatus: PropTypes.shape({}).isRequired,
};
BudgetRow.defaultProps = {
  showAssignments: false,
  canUpdateBudget: false,
  canViewAmounts: false,
  selected: false,
  disableSelect: false,
};

export default BudgetRow;
